import { NumberInput } from '@mantine/core';
import type { ZodTypeAny } from 'zod';
import { z } from 'zod';
import { useEffect, useRef } from 'react';

import { Tooltip } from '~/domains/common/components/Tooltip/Tooltip';
import dt from '~/domains/contest/domains/managev2/domains/common/testing.const';

import type { BaseInterativeField, FieldDescriptor, StaticFieldProps } from '../../types/common';
import { FieldMeta } from '../common/FieldMeta';

export type NumberFieldType = BaseInterativeField & {
  type: 'NumberField';
  validation?: BaseInterativeField['validation'] & {
    min?: number;
    max?: number;
    maxAllow?: number;
  };
  params?: {
    icon?: string;
    autoFocus?: boolean;
    labelPrefix?: string;
    labelPostfix?: string;
  };
  isDisabled?: boolean;
  tooltip?: string;
};

function NumberFieldElement({
  field,
  value,
  path,
  onValueChange,
  errors,
  isTouched,
  propagateFieldLabelToParent,
}: StaticFieldProps<NumberFieldType>) {
  const maxAllow = field.validation?.maxAllow ?? field.validation?.max;
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (field.params?.autoFocus) {
      ref.current?.select();
    }
  }, [field.params?.autoFocus]);

  // This is here just to prevent rerenders since this function is being
  // recreated on every render
  const isPropagateFieldLabelToParent = typeof propagateFieldLabelToParent === 'function';
  useEffect(() => {
    if (!isPropagateFieldLabelToParent) return;

    propagateFieldLabelToParent(
      path,
      typeof value === 'number'
        ? `${field.params?.labelPrefix ?? ''}${value}${field.params?.labelPostfix ?? ''}`
        : undefined
    );

    return () => {
      propagateFieldLabelToParent(path, undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPropagateFieldLabelToParent, value]);

  return (
    <Tooltip
      key={field.key}
      withinPortal
      position="top-start"
      label={field?.tooltip}
      isRendered={!!field?.tooltip}
    >
      <div>
        <FieldMeta field={field} errors={errors} isTouched={isTouched} />
        <NumberInput
          ref={ref}
          value={typeof value === 'number' ? value : 0}
          min={field.validation?.min}
          max={maxAllow}
          defaultValue={field?.defaultValue as number}
          onChange={onValueChange}
          data-test-id={dt.numberField}
          icon={field.params?.icon}
          autoFocus={field.params?.autoFocus}
        />
      </div>
    </Tooltip>
  );
}

const createValidationSchema = (field: NumberFieldType) => {
  let fieldSchema: ZodTypeAny = z.number({
    required_error: 'Field is required',
  });
  if (field.validation?.max) {
    // @ts-expect-error should be ZodNumber
    fieldSchema = fieldSchema.max(field.validation.max, {
      message: `Maximum allowed value is ${field.validation.max}`,
    });
  }
  if (field.validation?.min) {
    // @ts-expect-error should be ZodNumber
    fieldSchema = fieldSchema.min(field.validation?.min, {
      message: `Minimum allowed value is ${field.validation.min}`,
    });
  }
  if (field.validation?.isOptional) {
    fieldSchema = fieldSchema.optional();
  }

  return fieldSchema;
};

const Descriptor: FieldDescriptor<NumberFieldType> = {
  FieldComponent: NumberFieldElement,
  name: 'NumberField',
  createValidationSchema,
};

export default Descriptor;
